import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { brandOr } from '../../utils/brand';
import { fmtBkk } from '../Bugify';
import { aiSuggestionsApi, AiSuggestion, AiSuggestionList } from '../../utils/api/aiSuggestions';
import { markDigestSeen } from './Digest';

// ── The AI improvement suggestions list (report 1492665d) ──────────────────────────────────────────────
// What the once-a-day digest opens (./Digest): the day's list, ranked by priority by the AI, each item a Fix
// or a Suggestion with the reports it is drawn from. Accept files it in Bugify as a suggestion under the
// name of the person who pressed it — it joins the queue like any report typed by hand; Dismiss keeps it on
// the list, marked and struck through, and it is not put forward again. A dozen items a day at most, so the
// list comes whole and needs no pages.
// Only the logins ticked to receive the suggestions (me.aiSuggestions) open it; for anyone else it asks the
// server nothing, since a refused call pages the admins.

const KIND_STYLE: Record<AiSuggestion['kind'], { bg: string; fg: string; bd: string }> = {
  fix: { bg: '#FCEBEB', fg: '#B91C1C', bd: '#F7C1C1' },
  suggestion: { bg: '#EFF6FF', fg: '#1D4ED8', bd: '#BFDBFE' },
};

export function AiSuggestionsView() {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const userId = me?.userId || '';
  const allowed = !!me?.aiSuggestions;
  const [data, setData] = useState<AiSuggestionList | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');   // the item being accepted or dismissed

  const load = useCallback(async () => {
    try {
      const d = await aiSuggestionsApi.list();
      setData(d);
      setErr('');
      // Opening the list is reading the digest: it does not come up again today.
      if (d.date && !d.seen) markDigestSeen(userId, d.date);
    } catch (e: any) { setErr(e?.message || String(e)); }
  }, [userId]);
  useEffect(() => { if (allowed) void load(); }, [allowed, load]);

  const act = async (id: string, accept: boolean) => {
    setBusy(id); setErr('');
    try {
      if (accept) await aiSuggestionsApi.accept(id);
      else await aiSuggestionsApi.dismiss(id);
      await load();
    } catch (e: any) { setErr(e?.message || String(e)); }
    finally { setBusy(''); }
  };

  const btn = (primary: boolean): React.CSSProperties => ({
    padding: '6px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', cursor: busy ? 'wait' : 'pointer',
    border: primary ? 'none' : '1px solid var(--border)', background: primary ? 'var(--accent)' : 'var(--bg-surface)', color: primary ? '#fff' : 'var(--text-secondary)',
  });

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 20px' }}>
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className="ti ti-bulb" style={{ fontSize: 22, color: 'var(--accent)' }} aria-hidden />
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{t('ais_title')}</div>
        </div>
        {allowed && data && data.date && (
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('ais_sub', { at: fmtBkk(data.generated_at), n: data.reports, days: data.days })}</div>
        )}
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginTop: 12 }}>{err}</div>}
        <div style={{ marginTop: 16 }}>
          {!allowed ? (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('ais_no_access')}</div>
          ) : !data ? (
            !err && <i className="ti ti-loader" style={{ fontSize: 22, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
          ) : !data.items.length ? (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('ais_empty')}</div>
          ) : data.items.map(it => {
            const k = KIND_STYLE[it.kind] || KIND_STYLE.suggestion;
            const gone = it.state === 'dismissed';
            return (
              <div key={it.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', marginBottom: 10, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, opacity: gone ? 0.6 : 1 }}>
                {/* Its place on the AI's ranking: 1 is the most important. */}
                <span style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 800 }}>{it.rank}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {/* Suggestion reads as Bugify's own word for it (bg_kind_suggestion). */}
                    <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 9px', borderRadius: 999, background: k.bg, color: k.fg, border: `1px solid ${k.bd}` }}>{t(it.kind === 'fix' ? 'ais_kind_fix' : 'bg_kind_suggestion')}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', textDecoration: gone ? 'line-through' : 'none' }}>{it.title}</span>
                  </div>
                  {it.screen && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('ais_screen', { s: it.screen })}</div>}
                  <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--text-secondary)', marginTop: 6 }}>{it.why}</div>
                  {it.sources.length > 0 && (
                    <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 6 }}>
                      {t('ais_sources')} {it.sources.map(s => `#${s.short}${s.title ? ` ${s.title}` : ''}`).join(' · ')}
                    </div>
                  )}
                  {it.state === 'accepted' && (
                    <div style={{ fontSize: 12, color: '#15803D', marginTop: 8 }}>
                      <i className="ti ti-check" style={{ fontSize: 13 }} aria-hidden /> {t('ais_accepted', { who: it.by_name || '', when: fmtBkk(it.at) })}
                      {' · '}<a href="/m/bugify" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>{t('ais_open_bugify')}</a>
                    </div>
                  )}
                  {gone && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 8 }}>{t('ais_dismissed', { who: it.by_name || '', when: fmtBkk(it.at) })}</div>}
                </div>
                {/* A dismissed one can still be accepted — a change of mind costs nothing. */}
                {it.state !== 'accepted' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => void act(it.id, true)} disabled={!!busy} title={t('ais_accept_hint')} style={btn(true)}>{t('ais_accept')}</button>
                    {!gone && <button onClick={() => void act(it.id, false)} disabled={!!busy} style={btn(false)}>{t('ais_dismiss')}</button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function AiSuggestions() {
  const { t } = useTranslation();
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => window.location.assign('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_ai_suggestions')}</div>
      </header>
      <AiSuggestionsView />
    </div>
  );
}
